import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Checkbox, FormControlLabel } from '@mui/material';
import InfoTransferPopUp from '@/components/SubCampaignHeader/InfoTransferPopUp/InfoTransferPopUp';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import UniversalChip from '@/components/UI/UniversalChip/UniversalChip';
import { subCampaignTabsToIndex, TEAMS_PERSON, tabSubCRToNotesState, errorTypes, STORAGE, LAST_OVERVIEW_VALUES, ROUTES, chipTypes, statusSubCampaign, isNotPRO } from '@/helpers/constants';
import { buildFeedbackSavePayload } from '@/containers/SubCampaignDetail/Feedback/feedback-utils';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import { getVersionUrlParam, hideFeedbackSubCR, isSubCR, isSummary } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useDownloadFile from '@/hooks/useDownloadFile';
import useError from '@/hooks/useError';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useSubCampaignValidate from '@/hooks/useSubCampaignValidate';
import useToastContext from '@/hooks/useToastContext';
import useVisibilityMatrix from '@/hooks/useVisibilityMatrix';
import { setCampaignHelp, toggleCampaignHistory } from '@/redux/Campaign/campaign.action';
import { getCampaignHistory, getCampaignInfo, getCampaignVehicles, getCampaingSummary } from '@/redux/Campaign/campaign.reducer';
import { setHeaderLanguage } from '@/redux/Header/header.action';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setHighlightedFields } from '@/redux/HighlightFields/highlightFields.action';
import { getHighlightedFields } from '@/redux/HighlightFields/highlightFields.reducer';
import { setSubCampaignFeedback, setSubCampaignNotesByTab, setSubCampaignOriginalValues } from '@/redux/SubCampaign/subCampaign.action';
import {
  getSubCampaign, getSubcampaignClassificationNotes, getSubcampaignHardwareNotes, getSubcampaignSoftwareNotes, getSubCampaignInfo,
  getSubCampaignFeedback, getSubCampaignOriginalValues, getSubcampaignVehiclesNotes, getSubcampaignDescriptionNotes, getIsVANSubCR,
  getSubCampaignSummary,
} from '@/redux/SubCampaign/subCampaign.reducer';
import { getUserInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import {
  AffectedVehicles, CampaignHistory, HelpTextState, HighlightedFields, Language, OriginalValues, SubCampaignFeedback, SubCampaignInfoOverview,
  SubCRNotesByTab, Summary, User,
} from '@/types';
import { StatusCampaign, StatusSubCampaign, VisibilityMatrixAction, VisibilityMatrixFeature, WorkflowType } from '@/types/Matrix';
import SplitSubCampaignPopup from './SplitSubCampaignPopup/SplitSubCampaignPopup';
import SummaryButton from './SummaryButton/SummaryButton';
import ZasmConnectionButton from './ZasmConnectionButton/ZasmConnectionButton';
import TabsComponent from '../TabsComponent/TabsComponent';
import styles from './SubCampaignHeader.module.scss';

type SubCampaignHeaderProps = {
  id: string;
  tab: string;
  tabComments: SubCRNotesByTab;
  subCampaignFeedback: SubCampaignFeedback;
  subCampaignInfo: SubCampaignInfoOverview;
  mainCampaignId: string;
  subCROriginalValues: OriginalValues;
  headerLanguage: Language;
  campaignHelp: HelpTextState;
  setSubCROriginalValues: (values: OriginalValues) => void;
  setHeaderLang: (lang: Language) => void;
  setHelp: (help: HelpTextState) => void;
  reloadSubCR: (reason?: string) => void;
  setNotesByTab: (notes: SubCRNotesByTab) => void;
  isVANSubCR: boolean;
  userInfo: User;
  setFeedback: (feedback: SubCampaignFeedback) => void;
  summary: Summary;
  affectedVehicles: AffectedVehicles;
  setURLsNotLoaded: (urls: string[]) => void;
  refreshSubCRVehicles: () => void;
  highlightedFields: HighlightedFields;
  setSelectedFields: (fields: HighlightedFields) => void;
  toggleHistory: (show: boolean) => void;
  campaignHistory: CampaignHistory;
};

const SubCampaignHeader: React.FC<SubCampaignHeaderProps> = (props) => {
  const { id, tab, tabComments, subCampaignFeedback, subCampaignInfo, mainCampaignId, subCROriginalValues, setSubCROriginalValues,
    headerLanguage, setHeaderLang, campaignHelp, setHelp, reloadSubCR, setNotesByTab, isVANSubCR, userInfo, setFeedback, summary, affectedVehicles,
    refreshSubCRVehicles, setURLsNotLoaded, toggleHistory, campaignHistory, highlightedFields, setSelectedFields } = props;
  const { version } = useParams();
  const { handleCriticalError } = useError();
  const [showTranslationsDialog, setShowTranslationsDialog] = useState(false);
  const [checkDoNotShowAgain, setCheckDoNotShowAgain] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [openSplitSubCampaign, setOpenSplitSubCampaign] = useState(false);
  const [openSubCRInfoTransfer, setOpenSubCRInfoTransfer] = useState(false);
  const [emptyCampaignNumberPopup, setEmptyCampaignNumberPopup] = useState(false);
  const { goTo } = useGoTo();
  const { t } = useTranslation();
  const { downloadPDFSubCR } = useDownloadFile();
  const { isButtonDisabled, isReadOnly } = useCRPermissionMatrix(subCampaignInfo.status, subCampaignInfo.authorization);
  const { canSee } = useVisibilityMatrix();
  const canSeeCR = (action: VisibilityMatrixAction) => canSee({
    feature: VisibilityMatrixFeature.SUB_CR,
    action: action,
    status: subCampaignInfo.status as StatusCampaign | StatusSubCampaign, // TODO Missing type on status
  }) || !isButtonDisabled(action as unknown as CR_PERMISSION_ACTIONS);
  const tabsWithNotes = ['description', 'classification', 'affected-hardware', 'affected-software', 'affected-vehicles'];
  const validateSubCampaignForm = useSubCampaignValidate();
  const { addToast, closeValidationToast } = useToastContext();
  const [isCreatingPdfSpinner, setIsCreatingPdfSpinner] = useState(false);
  const { setLoading } = useGlobalSpinnerContext();
  const hideFeedback = hideFeedbackSubCR(subCampaignInfo);
  const summaryButtonsInFullSubCR = hideFeedbackSubCR(subCampaignInfo, [statusSubCampaign.IN_ANALYSIS]);
  const splitSubCampaignDisabled = isButtonDisabled(CR_PERMISSION_ACTIONS.SPLIT_SUB_CAMPAIGN, subCampaignInfo.workflowType as WorkflowType) && affectedVehicles?.affectedSeries?.length !== 0;
  const checkDisabled = useCheckDisabled(true, true);

  useEffect(() => {
    const mustShowDialog = subCampaignInfo?.plantLanguage?.length > 1
      && headerLanguage !== subCampaignInfo.plantLanguage
      && localStorage.getItem(STORAGE.TRANSLATION_DIALOG_SHOW) !== 'true';
    setShowTranslationsDialog(mustShowDialog);
  }, [headerLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem(STORAGE.PREVIOUS_TAB, tab);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const campaignSelected = useMemo(() => subCampaignFeedback.campaigns[0], [subCampaignFeedback]);

  const createZASMDisabled = campaignSelected?.zasmIntaktSynchronization?.updatedByZASM
    || campaignSelected?.campaignManagerEntry?.campaignNumber !== '' || checkDisabled;

  const filterEmptyNotes = (notes) => notes.filter((note) => note !== '' && note !== null);

  const validateSubCampaign = async () => {
    const isSubCampaingFormValid = await validateSubCampaignForm();

    return isSubCampaingFormValid;
  };

  const putFeedback = useApiRequest(campaignServices.putFeedback);
  const saveComments = useApiRequest(campaignServices.saveComments);

  const onSetSubCROriginValues = () => {
    const values = {
      ...subCROriginalValues,
      hasChange: false,
    };
    setSubCROriginalValues(values);
  };

  const saveSubCR = async (fromShowSummaryBtn = false): Promise<string | undefined> => {
    if ((isReadOnly() || isSaving) && !subCROriginalValues.hasChange) return;

    setIsSaving(true);
    closeValidationToast();

    if (['feedback'].includes(tab)) {
      let saveCodeError = '';

      if (subCampaignFeedback.campaigns.length > 1 && subCampaignFeedback.campaigns.filter((cr) => cr.campaignManagerEntry.campaignNumber === '')?.length > 0 && fromShowSummaryBtn) {
        validateSubCampaignForm();
        setIsSaving(false);

        return;
      }

      const result = await putFeedback(id, buildFeedbackSavePayload(subCampaignFeedback))
        .catch((err) => {
          if (err.name !== errorTypes.ABORT_ERROR) {
            if (err.code === errorTypes.CAMPAIGN_NUMBER_ALREADY_EXISTS) {
              addToast(t('feedback.zasmErrorCRNumber', { x: err.message.split(',')[0], y: err.message.split(',')[1] }), 'Error');
            } else {
              addToast(err.message, 'ERROR');
            }
          }
          saveCodeError = err.code;
        });

      // check if save was 200
      if (fromShowSummaryBtn && typeof result === 'string') await validateSubCampaign();
      if (![errorTypes.CAMPAIGN_NUMBER_ALREADY_EXISTS, errorTypes.duplicate_campaign_number_in_sub_campaign].includes(saveCodeError)) {
        onSetSubCROriginValues();
      }
      setFeedback(subCampaignFeedback);
      setIsSaving(false);

      if (saveCodeError) return saveCodeError;
    } else if (tabsWithNotes.includes(tab)) {
      const notes = tabComments[tab];
      const cleanNotes = filterEmptyNotes(notes);
      const dataNotes = { notes: cleanNotes };
      const tabModule = tab.replaceAll('-', '_');
      // real await when wait for a result

      const result = await saveComments(id, tabModule, dataNotes)
        .catch((err) => {
          handleCriticalError(err);
        });
      onSetSubCROriginValues();
      setIsSaving(false);

      if (notes.length !== cleanNotes.length) {
        addToast(t('alert.emptyNotesAdvice'), 'Warning');
        setNotesByTab({ tab: tabSubCRToNotesState[tab], notes: cleanNotes });
      }
    } else {
      setIsSaving(false);
    }
  };

  const goBack = async () => {
    const lastOverview = localStorage.getItem(STORAGE.LAST_OVERVIEW);
    if (!isReadOnly()) await saveSubCR();
    goTo(lastOverview === LAST_OVERVIEW_VALUES.CAMPAIGN_OVERVIEW ? ROUTES.CR_OVERVIEW : ROUTES.SUB_CR_OVERVIEW);
  };

  const tabsValues = (tabSelected: string) => {
    const tabsIndex = subCampaignTabsToIndex(hideFeedback);
    const selectedIndex = tabsIndex[tabSelected];

    if (selectedIndex === undefined && tabSelected && tabSelected !== '') {
      const fallbackTab = hideFeedback ? 'description' : 'feedback';
      navigateTo(fallbackTab);

      return tabsIndex[fallbackTab];
    }

    return selectedIndex !== undefined ? selectedIndex : tabsIndex[hideFeedback ? 'description' : 'feedback'];
  };

  const handleNavClick = async (path) => {
    if (subCROriginalValues?.hasChange) {
      await saveSubCR();
    }
    goTo(path);
  };

  const showHelp = () => {
    setHelp({
      ...campaignHelp,
      show: !campaignHelp?.show,
    });
  };

  const print = async () => {
    setIsCreatingPdfSpinner(true);

    if (subCROriginalValues?.hasChange) await saveSubCR();
    await downloadPDFSubCR(id, headerLanguage);
    setIsCreatingPdfSpinner(false);
  };

  const languageSelectorLabels = () => {
    const nonDefault = subCampaignInfo.activeLanguages?.filter((active) => active !== subCampaignInfo.plantLanguage);
    let label = t('campaign.hideTranslation', { lang: t(`languageChip.${nonDefault}`) });

    if (subCampaignInfo?.plantLanguage === headerLanguage) {
      label = t('campaign.showTranslation', { lang: t(`languageChip.${nonDefault}`) });
    }

    return label;
  };

  const handleClickShowTranslation = () => {
    setHeaderLang(headerLanguage === Language.EN ? Language.DE : Language.EN);
  };

  const showContactPersonInfo = (user) => {
    window.open(`${TEAMS_PERSON}${user}`, '_blank');
  };

  const onCloseTranslationInfoDialog = () => {
    localStorage.setItem(STORAGE.TRANSLATION_DIALOG_SHOW, checkDoNotShowAgain.toString());
    setShowTranslationsDialog(false);
  };

  const doNotShowAgainCheck = (
    <FormControlLabel
      control={(
        <Checkbox
          color="primary"
          checked={checkDoNotShowAgain}
          onChange={() => setCheckDoNotShowAgain(!checkDoNotShowAgain)}
        />
      )}
      label={t('translations.translationInfoCheck')}
    />
  );

  const navigateTo = (newTab) => goTo(`/sub-campaign/${id}${getVersionUrlParam(version)}/${newTab}`);

  const toggleHistoryMenu = () => {
    saveSubCR();
    toggleHistory(!campaignHistory?.show);
  };

  const handleShowSummary = async () => {
    setLoading(true);

    if ((subCROriginalValues?.hasChange || subCampaignInfo.status === statusSubCampaign.IN_ANALYSIS) && !isReadOnly()) {
      const hasErrorSave = await saveSubCR(true);

      if (hasErrorSave) {
        setLoading(false);

        return;
      }
    }

    if (subCampaignInfo.authorization?.readOnly || hideFeedback) {
      navigateTo('summary');
    } else {
      const isValid = await validateSubCampaign();

      if (isValid) {
        const emptyCampaigns = subCampaignFeedback?.campaigns?.filter((cr) => !cr?.campaignManagerEntry?.campaignNumber);

        if (emptyCampaigns.length > 0) {
          setEmptyCampaignNumberPopup(true);
        } else {
          localStorage.setItem(STORAGE.PREVIOUS_TAB, 'summary');
          goTo(`/sub-campaign/${id}/summary`);
          closeValidationToast();
        }
      }
    }
    setLoading(false);
  };

  const handleSubmitSelectedFields = async () => {
    setLoading(true);
    try {
      await campaignServices.saveSelectedFields(mainCampaignId, { selectedFields: highlightedFields.selectedFields });
      setSelectedFields({
        ...highlightedFields,
        isSelectionMode: false,
      });
      await campaignServices.changeDecision(id, { comment: highlightedFields.reviewerComment }, true);
      addToast(t('alert.successfullySent'));
      reloadSubCR();
      goTo(`/sub-campaign/${id}/feedback`, 250);
    } catch (error) {
      addToast(error instanceof Error ? error.message : t('errors.errorBoundaryMessage1'), 'ERROR');
    } finally {
      setLoading(false);
    }
  };

  const onClosePopupEmptyCampaign = (tabToGo: 'summary' | 'feedback') => {
    setEmptyCampaignNumberPopup(false);
    localStorage.setItem(STORAGE.PREVIOUS_TAB, tabToGo);
    goTo(`/sub-campaign/${id}/${tabToGo}`);
    closeValidationToast();
  };

  // save feedback if click on create intakt cr
  useEffect(() => {
    const saveFeedbackIfNeeded = async () => {
      if (subCROriginalValues?.hasChange && subCampaignFeedback?.saveNeeded) {
        setLoading(true);
        await saveSubCR(false);
        setLoading(false);
      }
    };
    saveFeedbackIfNeeded();
  }, [subCampaignFeedback?.saveNeeded]); // eslint-disable-line react-hooks/exhaustive-deps

  const zasmButtons = () => ((
    <div className={styles.RowButtons}>
      <ZasmConnectionButton
        id={id}
        createZASMDisabled={createZASMDisabled}
        isSelection
        version={version}
        setOnClose={() => setEmptyCampaignNumberPopup(false)}
        setURLsNotLoaded={setURLsNotLoaded}
      />
      <ZasmConnectionButton
        id={id}
        createZASMDisabled={createZASMDisabled}
        isSelection={false}
        setOnClose={() => setEmptyCampaignNumberPopup(false)}
        version={version}
        setURLsNotLoaded={setURLsNotLoaded}
      />
    </div>
  ));

  return (
    <div className={styles.Header} id="subcampaign_header">
      <div className={styles.HeaderInfo}>
        <div className={`${styles.Flex} ${styles.TitleHeader}`}>
          <div className={styles.Flex}>
            <IcoButtonSimple
              id="btn-exit-campaign"
              icon="icon-pfeil-chevron-links"
              tooltipText={t('campaign.backToOverview')}
              handleClick={goBack}
            />
            <span className={styles.CampaignCode}>{id}</span>
          </div>
          <div className={styles.Flex}>
            {isSummary() && canSeeCR(VisibilityMatrixAction.FULL_CR_SUB_CR) && (
              <SimpleButton
                id="show-full-cr-btn"
                color="primary"
                buttonText={t('buttonsHeader.showFullCR')}
                handleClick={() => navigateTo(hideFeedback ? 'description' : 'feedback')}
              />
            )}
            {isSaving
              ? (<div className={styles.SpinnerHeader}><Spinner small isVisible /></div>)
              : (
                <IcoButtonSimple
                  disabled={!subCROriginalValues?.hasChange || isReadOnly()}
                  id="btn-save-campaign"
                  icon="icon-diskette-speichern"
                  tooltipText={t('campaign.save')}
                  handleClick={saveSubCR}
                />
              )}
            <div className={styles.ButtonSave}>
              {isCreatingPdfSpinner && (<div data-testId="print-spinner" className={styles.SpinnerHeader}><Spinner small isVisible /></div>)}
              {!isCreatingPdfSpinner && (
                <IcoButtonSimple
                  id="btn-print-campaign"
                  icon="icon-drucken"
                  tooltipText={t('campaign.print')}
                  handleClick={print}
                />
              )}
            </div>
            {(!isVANSubCR && isNotPRO) && (
              <>
                <IcoButtonSimple
                  id="btn-split-subcampaign"
                  icon="icon-schere-ausschneiden"
                  tooltipText={t('campaign.splitSubCampaign')}
                  handleClick={() => setOpenSplitSubCampaign(true)}
                  active={openSplitSubCampaign}
                  disabled={splitSubCampaignDisabled}
                />

                {subCampaignInfo.status === statusSubCampaign.IN_ANALYSIS && (
                  <IcoButtonSimple
                    id="btn-transfer-subcampaign-info"
                    icon="icon-doppelpfeil-uebertragen"
                    tooltipText={t('campaign.transferSubCampaignInfo')}
                    handleClick={() => setOpenSubCRInfoTransfer(true)}
                    active={openSubCRInfoTransfer}
                    disabled={splitSubCampaignDisabled}
                  />
                )}
              </>
            )}
            <IcoButtonSimple
              id="btn-help-subcampaign"
              icon="icon-hilfe"
              tooltipText={t('campaign.showHelp')}
              handleClick={showHelp}
              active={campaignHelp?.show}
            />
          </div>
        </div>
        <div className={styles.SubTitleHeader}>
          <div>{t(`subCampaign.${subCampaignInfo.status}`, { plantNumber: subCampaignInfo?.plant })}</div>
          <UniversalChip
            id="subCR-campaign-type"
            chipType={chipTypes.WITHOUT_FUNCTION}
            text={subCampaignInfo?.type}
          />
          <UniversalChip
            id="subCR-version"
            chipType={chipTypes.WITH_FUNCTION}
            onClick={toggleHistoryMenu}
            text={`${t('campaignRequests.version')}: ${subCampaignInfo?.version}`}
            active={campaignHistory?.show}
          />
          <UniversalChip
            id="subCR-creation-user"
            chipType={chipTypes.WITH_FUNCTION}
            fieldPath={['creationUser', 'summary']}
            onClick={() => showContactPersonInfo(subCampaignInfo.creationUser?.code)}
            icon={<i className="di icon-person" />}
            text={subCampaignInfo.creationUser?.summary}
          />
          {(subCampaignInfo?.assignedUser?.summary !== '' && !isVANSubCR) && (
            <UniversalChip
              id="campaign-manager-button"
              chipType={chipTypes.WITH_FUNCTION}
              fieldPath={['assignedUser', 'summary']}
              onClick={() => showContactPersonInfo(subCampaignInfo.assignedUser?.code)}
              icon={<i className="di icon-werk" />}
              text={subCampaignInfo.assignedUser?.summary}
            />
          )}
          {subCampaignInfo?.activeLanguages?.length > 1
            && (
              <UniversalChip
                key="lang-button"
                id="lang-button"
                onClick={handleClickShowTranslation}
                chipType={chipTypes.WITH_FUNCTION}
                text={languageSelectorLabels()}
                active={headerLanguage !== subCampaignInfo?.defaultLanguage}
                dot="Alert"
                icon={<i className="di icon-weltkugel" />}
              />
            )}
        </div>
        <div className={styles.TabHeader}>
          <TabsComponent
            isSubCR
            handleNavClick={handleNavClick}
            tabSelected={tabsValues(tab)}
          />
        </div>
        <div className={styles.ButtonsContainer}>
          {(isSummary() || summaryButtonsInFullSubCR) && (
            <SummaryButton
              subCRInfo={subCampaignInfo}
              subCRId={id}
              saveSubCR={saveSubCR}
              subCRHasChanges={subCROriginalValues?.hasChange || false}
              reloadSubCR={reloadSubCR}
              isVANSubCR={isVANSubCR}
              userInfo={userInfo}
              summary={summary}
            />
          )}

          {(
            !highlightedFields?.isSelectionMode
            && (
              (!isSummary() && summaryButtonsInFullSubCR)
                ? canSeeCR(VisibilityMatrixAction.SHOW_SUMMARY_SUB_CR)
                : (!isSummary() && canSeeCR(VisibilityMatrixAction.SHOW_SUMMARY_SUB_CR))
            )
            && (
              <Button
                id="show-summary-btn"
                handleClick={handleShowSummary}
                buttonStyle="RoundButton"
                btnText={t('buttonsHeader.showSummary')}
              />
            ))}

          {highlightedFields?.isSelectionMode && (
            <Button
              id="send-selection-btn"
              buttonStyle="RoundButton"
              btnText={t('buttonsHeader.sendSelection') as string}
              handleClick={handleSubmitSelectedFields}
            />
          )}
        </div>
      </div>
      {showTranslationsDialog && (
        <ConfirmDialog
          type="information"
          confirm={onCloseTranslationInfoDialog}
          title={t('translations.translationInfoTitle')}
          message={t('translations.translationInfoMessage')}
          confirmBtnText={t('dialog.ok')}
        >
          {doNotShowAgainCheck}
        </ConfirmDialog>
      )}
      {emptyCampaignNumberPopup && (
        <ConfirmDialog
          type="information"
          fullWidth
          title={t('dialog.subCRWithEmptyCampaign.title')}
          message={t('dialog.subCRWithEmptyCampaign.message')}
          confirmBtnText={t('dialog.continue')}
          confirm={() => onClosePopupEmptyCampaign('summary')}
          closeBtnText={t('dialog.edit')}
          reject={() => onClosePopupEmptyCampaign('feedback')}
        >
          {zasmButtons()}
        </ConfirmDialog>
      )}
      {openSplitSubCampaign && (
        <SplitSubCampaignPopup
          subCRId={id}
          plant={subCampaignInfo?.plant}
          affectedVehicles={affectedVehicles}
          reloadSubCR={refreshSubCRVehicles}
          closePopup={() => setOpenSplitSubCampaign(false)}
        />
      )}
      {openSubCRInfoTransfer && (
        <InfoTransferPopUp
          subCRId={id}
          reloadSubCR={refreshSubCRVehicles}
          closePopup={() => setOpenSubCRInfoTransfer(false)}
        />
      )}
    </div>
  );
};

const actionCreators = {
  setHeaderLang: setHeaderLanguage,
  setSubCROriginalValues: setSubCampaignOriginalValues,
  setHelp: setCampaignHelp,
  setFeedback: setSubCampaignFeedback,
  setNotesByTab: setSubCampaignNotesByTab,
  setSelectedFields: setHighlightedFields,
  toggleHistory: toggleCampaignHistory,
};

const mapStateToProps = (state) => ({
  subCampaign: getSubCampaign(state),
  subCampaignFeedback: getSubCampaignFeedback(state),
  tabComments: {
    description: getSubcampaignDescriptionNotes(state),
    classification: getSubcampaignClassificationNotes(state),
    'affected-hardware': getSubcampaignHardwareNotes(state),
    'affected-software': getSubcampaignSoftwareNotes(state),
    'affected-vehicles': getSubcampaignVehiclesNotes(state),
  },
  subCampaignInfo: getSubCampaignInfo(state),
  mainCampaignId: getCampaignInfo(state).id,
  headerLanguage: getHeaderLanguage(state) || getSubCampaignInfo(state)?.plantLanguage,
  subCROriginalValues: getSubCampaignOriginalValues(state),
  isVANSubCR: getIsVANSubCR(state),
  userInfo: getUserInfo(state),
  summary: isSubCR() ? (getSubCampaignSummary(state)?.module || {}) : getCampaingSummary(state),
  affectedVehicles: getCampaignVehicles(state),
  highlightedFields: getHighlightedFields(state),
  campaignHistory: getCampaignHistory(state),
});

export default (connect(mapStateToProps, actionCreators)(SubCampaignHeader));
